// Theme system — visual presets for the environment, battle-map
// overlays, and title screen. Themes only change the *vibe* — gameplay
// signals (player sprites, HUD bars, faction icons) stay consistent
// across themes so the game still reads clearly.
//
// Pick a theme via `?theme=<id>` URL param. Selection is persisted in
// localStorage for the next visit.

export type Theme = {
  id: string;
  name: string;
  tagline: string;

  // 3D scene
  sky: number;
  fog: number;
  fogNear: number;
  fogFar: number;
  ambient: number;

  // Battle map overlay (src/scene/battle_map.ts)
  tintVirgin: number;       // left-half ground tint
  tintChad: number;  // right-half ground tint
  midline: number;         // glowing center strip
  baseVirgin: number;       // faction base ring + pillar color
  baseChad: number;

  // Title screen (src/ui/team_picker.ts)
  titleBackground: string;      // CSS gradient string
  titleVirginGradient: string;   // CSS linear-gradient for VIRGIN word
  titleChadGradient: string;
  titleVs: string;              // CSS color
  titleSparkleHueCold: number;  // 0..360 HSL hue
  titleSparkleHueWarm: number;
  titleSeamColor: string;       // jagged midline in the split bg
  titleAccentCold: string;
  titleAccentWarm: string;

  // Optional full-screen CSS overlay effect. Mounted in main.ts.
  // null / undefined = no overlay.
  overlay?: 'scanlines' | 'halftone' | 'posterize' | 'grain' | null;
};

export const THEMES: Theme[] = [
  {
    id: 'y2k',
    name: 'Y2K Chrome',
    tagline: 'sunset grid, holographic chrome — the flagship',
    sky: 0x0a0220,
    fog: 0x2a0a4a,
    fogNear: 55,
    fogFar: 160,
    ambient: 0xffccff,
    tintVirgin: 0x00f0ff,
    tintChad: 0xff1493,
    midline: 0xfff200,
    baseVirgin: 0x66f7ff,
    baseChad: 0xff66c4,
    titleBackground:
      'radial-gradient(ellipse at 50% 110%, #fff200 0%, #ff8c00 18%, #ff1493 34%, #7a0a6a 55%, #2a0a4a 75%, #0a0220 100%)',
    titleVirginGradient:
      'linear-gradient(90deg, #aaffff 0%, #00f0ff 40%, #ffffff 55%, #00c8e0 70%, #0a6b8a 100%)',
    titleChadGradient:
      'linear-gradient(90deg, #ffcfe8 0%, #ff1493 40%, #ffffff 55%, #ff1493 70%, #8a0a52 100%)',
    titleVs: '#fff200',
    titleSparkleHueCold: 185,
    titleSparkleHueWarm: 320,
    titleSeamColor: 'rgba(255,242,0,.95)',
    titleAccentCold: '#0a6b8a',
    titleAccentWarm: '#8a0a52',
    overlay: 'scanlines',
  },
  {
    id: 'noir',
    name: 'Noir',
    tagline: 'classic midnight city',
    sky: 0x0e1420,
    fog: 0x0e1420,
    fogNear: 80,
    fogFar: 200,
    ambient: 0xffffff,
    tintVirgin: 0x3b82f6,
    tintChad: 0xfb923c,
    midline: 0xfde047,
    baseVirgin: 0x60a5fa,
    baseChad: 0xfb923c,
    titleBackground:
      'linear-gradient(135deg, #050814 0%, #0c1733 35%, #1a0f23 50%, #2a0e12 65%, #0a0611 100%)',
    titleVirginGradient:
      'linear-gradient(90deg, #93c5fd 0%, #60a5fa 40%, #e0f2fe 55%, #60a5fa 70%, #3b82f6 100%)',
    titleChadGradient:
      'linear-gradient(90deg, #fdba74 0%, #fb923c 40%, #fef3c7 55%, #fb923c 70%, #ea580c 100%)',
    titleVs: '#fde047',
    titleSparkleHueCold: 205,
    titleSparkleHueWarm: 28,
    titleSeamColor: 'rgba(253,224,71,.85)',
    titleAccentCold: '#1e40af',
    titleAccentWarm: '#b45309',
  },
  {
    id: 'neon',
    name: 'Neon Grid',
    tagline: 'cyberpunk arcade fever',
    sky: 0x0a0a1a,
    fog: 0x1a0a2e,
    fogNear: 60,
    fogFar: 160,
    ambient: 0xccaaff,
    tintVirgin: 0xa855f7,
    tintChad: 0x22d3ee,
    midline: 0xec4899,
    baseVirgin: 0xc084fc,
    baseChad: 0x67e8f9,
    titleBackground:
      'linear-gradient(135deg, #000000 0%, #0a001a 30%, #1a0033 50%, #001a2a 70%, #000000 100%)',
    titleVirginGradient:
      'linear-gradient(90deg, #e9d5ff 0%, #c084fc 40%, #f0abfc 55%, #a855f7 70%, #7e22ce 100%)',
    titleChadGradient:
      'linear-gradient(90deg, #cffafe 0%, #67e8f9 40%, #ecfeff 55%, #22d3ee 70%, #0891b2 100%)',
    titleVs: '#ec4899',
    titleSparkleHueCold: 285,
    titleSparkleHueWarm: 185,
    titleSeamColor: 'rgba(236,72,153,.9)',
    titleAccentCold: '#581c87',
    titleAccentWarm: '#155e75',
  },
  {
    id: 'sakura',
    name: 'Sakura Dawn',
    tagline: 'cherry blossom twilight',
    sky: 0x2a1b3e,
    fog: 0x5b4d7e,
    fogNear: 70,
    fogFar: 180,
    ambient: 0xffddee,
    tintVirgin: 0xec4899,
    tintChad: 0xfbbf24,
    midline: 0xfce7f3,
    baseVirgin: 0xf9a8d4,
    baseChad: 0xfcd34d,
    titleBackground:
      'linear-gradient(135deg, #1e1b2e 0%, #3d2844 30%, #5b2d4e 50%, #4a2642 70%, #1e1b2e 100%)',
    titleVirginGradient:
      'linear-gradient(90deg, #fbcfe8 0%, #f9a8d4 40%, #fdf2f8 55%, #ec4899 70%, #be185d 100%)',
    titleChadGradient:
      'linear-gradient(90deg, #fef3c7 0%, #fcd34d 40%, #fffbeb 55%, #fbbf24 70%, #d97706 100%)',
    titleVs: '#fce7f3',
    titleSparkleHueCold: 330,
    titleSparkleHueWarm: 45,
    titleSeamColor: 'rgba(252,231,243,.85)',
    titleAccentCold: '#9f1239',
    titleAccentWarm: '#92400e',
  },
  {
    id: 'synthwave',
    name: 'Synthwave',
    tagline: 'retro sunset horizon',
    sky: 0x2d1b47,
    fog: 0x5b2a8c,
    fogNear: 70,
    fogFar: 180,
    ambient: 0xffccdd,
    tintVirgin: 0x8b5cf6,
    tintChad: 0xf43f5e,
    midline: 0xff00ff,
    baseVirgin: 0xa78bfa,
    baseChad: 0xfb7185,
    titleBackground:
      'linear-gradient(180deg, #1a0033 0%, #330066 25%, #662244 50%, #993322 75%, #cc4400 100%)',
    titleVirginGradient:
      'linear-gradient(90deg, #ddd6fe 0%, #a78bfa 40%, #ede9fe 55%, #8b5cf6 70%, #5b21b6 100%)',
    titleChadGradient:
      'linear-gradient(90deg, #fecdd3 0%, #fb7185 40%, #ffe4e6 55%, #f43f5e 70%, #9f1239 100%)',
    titleVs: '#ff00ff',
    titleSparkleHueCold: 270,
    titleSparkleHueWarm: 340,
    titleSeamColor: 'rgba(255,0,255,.9)',
    titleAccentCold: '#4c1d95',
    titleAccentWarm: '#881337',
  },
  {
    id: 'gameboy',
    name: 'Game Boy DMG',
    tagline: '4-shade green monochrome tribute',
    sky: 0x0f380f,
    fog: 0x306230,
    fogNear: 50,
    fogFar: 140,
    ambient: 0x9bbc0f,
    tintVirgin: 0x306230,
    tintChad: 0x8bac0f,
    midline: 0x9bbc0f,
    baseVirgin: 0x0f380f,
    baseChad: 0x9bbc0f,
    titleBackground:
      'linear-gradient(135deg, #0f380f 0%, #306230 40%, #8bac0f 70%, #9bbc0f 100%)',
    titleVirginGradient:
      'linear-gradient(90deg, #9bbc0f 0%, #8bac0f 40%, #9bbc0f 55%, #306230 70%, #0f380f 100%)',
    titleChadGradient:
      'linear-gradient(90deg, #9bbc0f 0%, #8bac0f 40%, #9bbc0f 55%, #306230 70%, #0f380f 100%)',
    titleVs: '#9bbc0f',
    titleSparkleHueCold: 90,
    titleSparkleHueWarm: 75,
    titleSeamColor: 'rgba(155,188,15,.9)',
    titleAccentCold: '#0f380f',
    titleAccentWarm: '#306230',
    overlay: 'posterize',
  },
  {
    id: 'crt',
    name: 'CRT Arcade',
    tagline: 'amber cabinet, scanlines, vignette',
    sky: 0x1a0f05,
    fog: 0x3d1e08,
    fogNear: 55,
    fogFar: 150,
    ambient: 0xffb070,
    tintVirgin: 0xff6b35,
    tintChad: 0xffcc33,
    midline: 0xfff2a8,
    baseVirgin: 0xff8c42,
    baseChad: 0xffd97d,
    titleBackground:
      'linear-gradient(135deg, #0a0502 0%, #1a0f05 35%, #3d1e08 55%, #5c2d0c 75%, #0a0502 100%)',
    titleVirginGradient:
      'linear-gradient(90deg, #ffd97d 0%, #ff8c42 40%, #fff2a8 55%, #ff6b35 70%, #c1410f 100%)',
    titleChadGradient:
      'linear-gradient(90deg, #fff2a8 0%, #ffd97d 40%, #fffbea 55%, #ffcc33 70%, #c28918 100%)',
    titleVs: '#fff2a8',
    titleSparkleHueCold: 25,
    titleSparkleHueWarm: 45,
    titleSeamColor: 'rgba(255,242,168,.9)',
    titleAccentCold: '#7c2d0a',
    titleAccentWarm: '#92571c',
    overlay: 'scanlines',
  },
  {
    id: 'manga',
    name: 'Manga Ink',
    tagline: 'B&W halftone, red accent',
    sky: 0xf5f5f5,
    fog: 0xd4d4d4,
    fogNear: 80,
    fogFar: 220,
    ambient: 0xffffff,
    tintVirgin: 0x171717,
    tintChad: 0xe5e5e5,
    midline: 0xdc2626,
    baseVirgin: 0x262626,
    baseChad: 0xd4d4d4,
    titleBackground:
      'linear-gradient(135deg, #f5f5f5 0%, #e5e5e5 35%, #ffffff 50%, #d4d4d4 65%, #f5f5f5 100%)',
    titleVirginGradient:
      'linear-gradient(90deg, #404040 0%, #171717 40%, #525252 55%, #000000 70%, #0a0a0a 100%)',
    titleChadGradient:
      'linear-gradient(90deg, #dc2626 0%, #b91c1c 40%, #ef4444 55%, #991b1b 70%, #7f1d1d 100%)',
    titleVs: '#dc2626',
    titleSparkleHueCold: 0,
    titleSparkleHueWarm: 0,
    titleSeamColor: 'rgba(220,38,38,.9)',
    titleAccentCold: '#171717',
    titleAccentWarm: '#991b1b',
    overlay: 'halftone',
  },
  {
    id: 'vapor',
    name: 'Vaporwave Mall',
    tagline: 'pink/cyan seapunk, 90s ad-break',
    sky: 0x1a0633,
    fog: 0x3d1e5b,
    fogNear: 65,
    fogFar: 170,
    ambient: 0xffccee,
    tintVirgin: 0xff77cc,
    tintChad: 0x77eedd,
    midline: 0xffb3e6,
    baseVirgin: 0xff99dd,
    baseChad: 0xaaffee,
    titleBackground:
      'linear-gradient(180deg, #1a0633 0%, #3d1e5b 25%, #7a2c82 50%, #d946ef 75%, #fbcfe8 100%)',
    titleVirginGradient:
      'linear-gradient(90deg, #fbcfe8 0%, #ff99dd 40%, #fce7f3 55%, #ff77cc 70%, #be185d 100%)',
    titleChadGradient:
      'linear-gradient(90deg, #cffafe 0%, #aaffee 40%, #ecfeff 55%, #77eedd 70%, #0e7490 100%)',
    titleVs: '#ffb3e6',
    titleSparkleHueCold: 320,
    titleSparkleHueWarm: 175,
    titleSeamColor: 'rgba(255,179,230,.85)',
    titleAccentCold: '#831843',
    titleAccentWarm: '#155e75',
    overlay: 'grain',
  },
  {
    id: 'ghibli',
    name: 'Dreamscape',
    tagline: 'soft pastel watercolor, golden hour',
    sky: 0xa7d8f0,
    fog: 0xc9e3e8,
    fogNear: 90,
    fogFar: 240,
    ambient: 0xfff4d6,
    tintVirgin: 0x7fb6d4,
    tintChad: 0xf0c987,
    midline: 0xfff4d6,
    baseVirgin: 0xa0c9dd,
    baseChad: 0xf4d9a0,
    titleBackground:
      'linear-gradient(135deg, #a7d8f0 0%, #c9e3e8 30%, #fff4d6 55%, #f4d9a0 80%, #e8b896 100%)',
    titleVirginGradient:
      'linear-gradient(90deg, #c3e3f1 0%, #7fb6d4 40%, #e0f2fe 55%, #5a9dc0 70%, #2f6a8a 100%)',
    titleChadGradient:
      'linear-gradient(90deg, #fff4d6 0%, #f0c987 40%, #fffbea 55%, #e6a865 70%, #b06f28 100%)',
    titleVs: '#fff4d6',
    titleSparkleHueCold: 200,
    titleSparkleHueWarm: 40,
    titleSeamColor: 'rgba(255,244,214,.85)',
    titleAccentCold: '#2f6a8a',
    titleAccentWarm: '#b06f28',
  },
  {
    id: 'hotline',
    name: 'Hotline Miami',
    tagline: 'hot pink + teal strobe, violent grain',
    sky: 0x12021a,
    fog: 0x3b0a3d,
    fogNear: 55,
    fogFar: 150,
    ambient: 0xff66cc,
    tintVirgin: 0xff1493,
    tintChad: 0x00e6cc,
    midline: 0xffff00,
    baseVirgin: 0xff3ea5,
    baseChad: 0x33f5dc,
    titleBackground:
      'linear-gradient(135deg, #000000 0%, #12021a 25%, #3b0a3d 50%, #1a0a2e 75%, #000000 100%)',
    titleVirginGradient:
      'linear-gradient(90deg, #ff99cc 0%, #ff1493 40%, #ffd1eb 55%, #c71585 70%, #831843 100%)',
    titleChadGradient:
      'linear-gradient(90deg, #99f6e4 0%, #00e6cc 40%, #ccfbf1 55%, #14b8a6 70%, #115e59 100%)',
    titleVs: '#ffff00',
    titleSparkleHueCold: 325,
    titleSparkleHueWarm: 175,
    titleSeamColor: 'rgba(255,255,0,.9)',
    titleAccentCold: '#831843',
    titleAccentWarm: '#115e59',
    overlay: 'grain',
  },
  {
    id: 'kombat',
    name: 'Arcade Pit',
    tagline: 'blood red + toxic green, CRT grit',
    sky: 0x0a0202,
    fog: 0x2a0808,
    fogNear: 55,
    fogFar: 150,
    ambient: 0xff9999,
    tintVirgin: 0xb91c1c,
    tintChad: 0x65a30d,
    midline: 0xfacc15,
    baseVirgin: 0xdc2626,
    baseChad: 0x84cc16,
    titleBackground:
      'linear-gradient(135deg, #000000 0%, #0a0202 25%, #2a0808 50%, #1a0a02 75%, #000000 100%)',
    titleVirginGradient:
      'linear-gradient(90deg, #fca5a5 0%, #dc2626 40%, #fecaca 55%, #991b1b 70%, #450a0a 100%)',
    titleChadGradient:
      'linear-gradient(90deg, #bef264 0%, #84cc16 40%, #d9f99d 55%, #4d7c0f 70%, #1a2e05 100%)',
    titleVs: '#facc15',
    titleSparkleHueCold: 0,
    titleSparkleHueWarm: 80,
    titleSeamColor: 'rgba(250,204,21,.9)',
    titleAccentCold: '#450a0a',
    titleAccentWarm: '#1a2e05',
    overlay: 'scanlines',
  },
  {
    id: 'minecraft',
    name: 'Overworld',
    tagline: 'earthy blocks, big-pixel sky',
    sky: 0x78a7ff,
    fog: 0xa0c4ff,
    fogNear: 80,
    fogFar: 220,
    ambient: 0xfff4b0,
    tintVirgin: 0x5a8f3a,
    tintChad: 0xa67040,
    midline: 0xf0c060,
    baseVirgin: 0x6fa84a,
    baseChad: 0xbf8a52,
    titleBackground:
      'linear-gradient(180deg, #78a7ff 0%, #a0c4ff 40%, #5a8f3a 65%, #3a6626 85%, #2d4d1c 100%)',
    titleVirginGradient:
      'linear-gradient(90deg, #aedc84 0%, #6fa84a 40%, #c9ed9e 55%, #3a6626 70%, #1f3914 100%)',
    titleChadGradient:
      'linear-gradient(90deg, #dcb07c 0%, #bf8a52 40%, #ecc898 55%, #8a5c2e 70%, #4a2d12 100%)',
    titleVs: '#f0c060',
    titleSparkleHueCold: 95,
    titleSparkleHueWarm: 30,
    titleSeamColor: 'rgba(240,192,96,.9)',
    titleAccentCold: '#1f3914',
    titleAccentWarm: '#4a2d12',
  },
];

const STORAGE_KEY = 'smp.theme';

export function getActiveTheme(): Theme {
  const qp = typeof location !== 'undefined'
    ? new URL(location.href).searchParams.get('theme')
    : null;
  const stored = typeof localStorage !== 'undefined'
    ? localStorage.getItem(STORAGE_KEY)
    : null;
  const want = qp ?? stored ?? 'y2k';
  return THEMES.find((t) => t.id === want) ?? THEMES[0];
}

export function setActiveTheme(id: string): void {
  if (!THEMES.some((t) => t.id === id)) return;
  try { localStorage.setItem(STORAGE_KEY, id); } catch { /* ignore */ }
}

export function cycleTheme(currentId: string): Theme {
  const idx = THEMES.findIndex((t) => t.id === currentId);
  const next = THEMES[(idx + 1) % THEMES.length];
  setActiveTheme(next.id);
  return next;
}
